import { Alert, Box, Dialog, DialogContent } from "@mui/material";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import {
  MaterialEditScope,
  UpdateBody,
} from "components/identifyEventsTool/hooks/useEventManagement";
import {
  HotkeyProvider,
  useHotkeyContext,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import {
  VideoControlProvider,
  useVideoControl,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { usePauseWhileOpen } from "components/scrapEyeDataDashboard/VideoPlayer/hooks/usePauseWhileOpen";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { LabellerDetails } from "src/store/api/generatedApi";
import { EditEventHeader } from "./EditEventHeader";
import { EditEventTimelineSection } from "./EditEventTimelineSection";
import { EditEventVideoSection } from "./EditEventVideoSection";
import { EDIT_EVENT_SCOPE, LABELLING_HOTKEY_SCOPES } from "../hotkeyScopes";
import { EventEditControls } from "./EventEditControls";
import { HotkeySafeCheckbox } from "../components/HotkeySafeCheckbox";
import { useVideoTimelineData } from "../hooks/useVideoTimelineData";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import {
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "components/manualReview/contexts/ManualReviewEventContext";
import { AreaSelectionModal } from "components/scrapEyeLabelling/modals/AreaSelectionModal";
import { useDisableHotkeyScopes } from "components/scrapEyeLabelling/hooks/useDisableHotkeyScopes";
import { useAreaLabelledEvent } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { clampStartToRange, clampToRange } from "../utils/editScope";
import { EditEventMassAdjustment } from "./EditEventMassAdjustment";
import { MassChangeValues } from "./MassAdjustmentChart";
import { TimeAxisLayout } from "./timeAxisLayout";
import { PlaybackControls } from "components/scrapEyeDataDashboard/VideoPlayer/components/PlaybackControls";
import { DIALOG_SKIP_BUTTON_SECONDS } from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import { useSkipAndStepHandlers } from "components/scrapEyeDataDashboard/VideoPlayer/hooks/useSkipAndStepHandlers";

type UpdateBodyWithReviewData = UpdateBody<ScrapEyesEventWithReviewData>;
interface EditEventModalProps {
  open: boolean;
  onClose: () => void;
  mergedEvent: ScrapEyesEventWithReviewData | null;
  eventToLabellerMapping: Record<number, LabellerDetails>;
  location: string;
  onUpdate?: (
    updates: UpdateBodyWithReviewData,
    materialEditScope: MaterialEditScope
  ) => void;
  onDelete?: () => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  areaColors?: Record<string, string>;
  minTimingBound?: Dayjs;
  maxTimingBound?: Dayjs;
  readOnly?: boolean;
  readOnlyReason?: "verified" | "outsideWindow";
}

const SCOPES_TO_DISABLE_AREA_MODAL: readonly string[] = [EDIT_EVENT_SCOPE]; // for re-rendering

const DISABLED_CONTROLS_SX = {
  opacity: 0.5,
  pointerEvents: "none",
} as const;

const EditEventModalContent = ({
  mergedEvent,
  eventToLabellerMapping,
  location,
  onUpdate,
  onDelete,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  areaColors,
  minTimingBound,
  maxTimingBound,
  readOnly = false,
  readOnlyReason = "verified",
}: Omit<
  EditEventModalProps,
  "open" | "mergedEvent" | "onUpdate" | "onDelete"
> & {
  mergedEvent: ScrapEyesEventWithReviewData;
  onUpdate: NonNullable<EditEventModalProps["onUpdate"]>;
  onDelete?: EditEventModalProps["onDelete"];
}) => {
  const { videoStart, videoEnd, originalEventsByLabeller, videoDuration } =
    useVideoTimelineData(mergedEvent, eventToLabellerMapping, location);

  const { getHotkey } = useHotkeyContext();
  const { actions: videoActions, state: videoState } = useVideoControl();
  const { handleSkipBackward, handleSkipForward } = useSkipAndStepHandlers({
    skipSeconds: DIALOG_SKIP_BUTTON_SECONDS,
    ceilingSeconds: videoDuration,
  });
  const playheadTime = useMemo(
    () => videoStart.add(videoState.currentTime, "second"),
    [videoStart, videoState.currentTime]
  );
  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [adjustedMassValues, setAdjustedMassValues] =
    useState<MassChangeValues | null>(null);
  const [pendingUpdates, setPendingUpdates] =
    useState<UpdateBodyWithReviewData>({});
  const [massDetached, setMassDetached] = useState(
    () => mergedEvent.massDetached ?? false
  );
  const [materialEditScope, setMaterialEditScope] =
    useState<MaterialEditScope>("withConnected");
  const [massChartTimeAxis, setMassChartTimeAxis] =
    useState<TimeAxisLayout | null>(null);

  const withAreaLabels = useAreaLabelledEvent(location);

  const workingEvent = useMemo(
    () =>
      withAreaLabels({
        ...mergedEvent,
        ...pendingUpdates,
      } as ScrapEyesEventWithReviewData),
    [mergedEvent, pendingUpdates, withAreaLabels]
  );

  useEffect(() => {
    videoActions.reset();
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets form state when switching events
    setAdjustedMassValues(null);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets form state when switching events
    setPendingUpdates({});
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs the detach toggle to the freshly-loaded event's persisted state
    setMassDetached(mergedEvent.massDetached ?? false);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets form state when switching events
    setMaterialEditScope("withConnected");
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- videoActions object is unstable (changes on canUndo/canRedo); only reset on event change
  }, [mergedEvent.id, videoActions.reset]);

  const { t } = useTenantTranslation();

  const handleLocalUpdate = useCallback(
    (updates: UpdateBodyWithReviewData) => {
      if (readOnly) return;
      setPendingUpdates((prev) => ({ ...prev, ...updates }));
    },
    [readOnly]
  );

  const handleMassChange = useCallback((values: MassChangeValues) => {
    setAdjustedMassValues(values);
  }, []);

  const handleToggleMassDetached = useCallback(() => {
    setMassDetached((previous) => !previous);
  }, []);

  const { modifiedEventIds, revertEvent, getConnectedEventIds } =
    useManualReviewEvents();
  const connectedEventCount = useMemo(
    () => getConnectedEventIds(mergedEvent.id).length,
    [getConnectedEventIds, mergedEvent.id]
  );

  const hasPendingChanges =
    Object.keys(pendingUpdates).length > 0 ||
    adjustedMassValues !== null ||
    massDetached !== (mergedEvent.massDetached ?? false) ||
    modifiedEventIds.has(mergedEvent.id);

  const handleDiscard = useCallback(() => {
    setPendingUpdates({});
    setAdjustedMassValues(null);
    setMassDetached(false);
    revertEvent(mergedEvent.id);
  }, [revertEvent, mergedEvent.id]);

  const commitPendingChanges = useCallback(() => {
    const allUpdates = { ...pendingUpdates };
    if (adjustedMassValues !== null) {
      allUpdates.mass = adjustedMassValues.mass ?? undefined;
      allUpdates.initialMass = adjustedMassValues.initialMass;
      allUpdates.finalMass = adjustedMassValues.finalMass;
    }
    if (massDetached) {
      allUpdates.mass = null;
      allUpdates.initialMass = null;
      allUpdates.finalMass = null;
    }
    if (massDetached !== (mergedEvent.massDetached ?? false)) {
      allUpdates.massDetached = massDetached;
    }
    if (Object.keys(allUpdates).length > 0) {
      onUpdate(allUpdates, materialEditScope);
    }
    setPendingUpdates({});
    setAdjustedMassValues(null);
  }, [
    pendingUpdates,
    adjustedMassValues,
    massDetached,
    mergedEvent.massDetached,
    materialEditScope,
    onUpdate,
  ]);

  const handleSave = useCallback(() => {
    commitPendingChanges();
    onClose();
  }, [commitPendingChanges, onClose]);

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete();
      onClose();
    }
  }, [onDelete, onClose]);

  const handleToggleResolved = useCallback(() => {
    handleLocalUpdate({ resolved: !workingEvent.resolved });
  }, [workingEvent.resolved, handleLocalUpdate]);

  const handlePickTiming = useCallback(
    (event: ScrapEyesEvent) => {
      handleLocalUpdate({
        start: clampStartToRange({
          time: event.start,
          lowerBound: minTimingBound,
          upperBound: maxTimingBound,
        }),
        end: clampToRange({
          time: event.end,
          lowerBound: minTimingBound,
          upperBound: maxTimingBound,
        }),
      });
    },
    [handleLocalUpdate, minTimingBound, maxTimingBound]
  );

  const handlePickArea = useCallback(
    (event: ScrapEyesEvent) => {
      handleLocalUpdate({ area: event.area ?? undefined });
    },
    [handleLocalUpdate]
  );

  const handlePickMaterial = useCallback(
    (event: ScrapEyesEvent) => {
      handleLocalUpdate({
        material: event.material ?? undefined,
        coMaterial: null,
      });
    },
    [handleLocalUpdate]
  );

  const handleAreaSelect = useCallback(
    (area: string) => {
      handleLocalUpdate({ area });
      setAreaModalOpen(false);
    },
    [handleLocalUpdate]
  );

  useHotkeys(
    getHotkey("nextItem"),
    () => {
      commitPendingChanges();
      onNavigateNext?.();
    },
    {
      scopes: [EDIT_EVENT_SCOPE],
      enabled: !!onNavigateNext,
      preventDefault: true,
    },
    [commitPendingChanges, onNavigateNext, getHotkey]
  );

  useHotkeys(
    getHotkey("previousItem"),
    () => {
      commitPendingChanges();
      onNavigatePrevious?.();
    },
    {
      scopes: [EDIT_EVENT_SCOPE],
      enabled: !!onNavigatePrevious,
      preventDefault: true,
    },
    [commitPendingChanges, onNavigatePrevious, getHotkey]
  );

  useHotkeys(
    getHotkey("toggleResolved"),
    handleToggleResolved,
    {
      scopes: [EDIT_EVENT_SCOPE],
      preventDefault: true,
    },
    [handleToggleResolved, getHotkey]
  );

  useHotkeys(
    "escape",
    () => {
      commitPendingChanges();
      onClose();
    },
    {
      enabled: true,
      enableOnFormTags: true,
      preventDefault: true,
      scopes: [EDIT_EVENT_SCOPE],
    },
    [commitPendingChanges, onClose]
  );

  const { handleUndo, handleRedo } = useManualReviewEvents();

  useHotkeys(
    "mod+z",
    (e) => {
      e.preventDefault();
      handleUndo();
    },
    { scopes: [EDIT_EVENT_SCOPE], enableOnFormTags: true },
    [handleUndo]
  );

  useHotkeys(
    "mod+shift+z, mod+y",
    (e) => {
      e.preventDefault();
      handleRedo();
    },
    { scopes: [EDIT_EVENT_SCOPE], enableOnFormTags: true },
    [handleRedo]
  );

  return (
    <>
      <EditEventHeader
        mergedEvent={workingEvent}
        onNavigatePrevious={onNavigatePrevious}
        onNavigateNext={onNavigateNext}
        onToggleResolved={handleToggleResolved}
        hasPendingChanges={hasPendingChanges}
        onDiscard={handleDiscard}
        readOnly={readOnly}
      />

      <DialogContent
        sx={{
          p: 0,
          maxHeight: "calc(90vh - 100px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {readOnly ? (
          <Alert severity="info" sx={{ mx: 2, mt: 1 }}>
            {t(
              readOnlyReason === "outsideWindow"
                ? "eventOutsideReviewPeriod"
                : "verifiedEventReadOnly"
            )}
          </Alert>
        ) : null}
        <EditEventVideoSection
          location={location}
          videoStart={videoStart}
          videoEnd={videoEnd}
          videoDuration={videoDuration}
        />

        <Box sx={{ px: 2, pb: 1 }}>
          <PlaybackControls
            isPlaying={videoState.isPlaying}
            playbackRate={videoState.playbackRate}
            onTogglePlayPause={videoActions.togglePlayPause}
            onSkipBackward={handleSkipBackward}
            onSkipForward={handleSkipForward}
            onSpeedChange={videoActions.setPlaybackRate}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            alignItems: "stretch",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <EditEventTimelineSection
              workingEvent={workingEvent}
              originalEventsByLabeller={originalEventsByLabeller}
              videoStart={videoStart}
              videoDuration={videoDuration}
              currentTime={videoState.currentTime}
              onSeek={videoActions.seekTo}
              onTimingChange={handleLocalUpdate}
              onPickTiming={handlePickTiming}
              onPickArea={handlePickArea}
              onPickMaterial={handlePickMaterial}
              minTimingBound={minTimingBound}
              maxTimingBound={maxTimingBound}
              alignedTimeAxis={massChartTimeAxis}
              readOnly={readOnly}
            />

            <Box sx={readOnly ? DISABLED_CONTROLS_SX : undefined}>
              <EditEventMassAdjustment
                mergedEvent={workingEvent}
                savedArea={mergedEvent.area}
                playheadTime={playheadTime}
                onMassChange={handleMassChange}
                onTimeAxisChange={setMassChartTimeAxis}
                isMassDetached={massDetached}
                onToggleMassDetached={handleToggleMassDetached}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: 400,
              bgcolor: "grey.50",
              overflow: "auto",
            }}
          >
            <Box sx={readOnly ? DISABLED_CONTROLS_SX : undefined}>
              <EventEditControls
                event={workingEvent}
                location={location}
                onChange={handleLocalUpdate}
                scope={EDIT_EVENT_SCOPE}
                enabled={!readOnly}
                onOpenAreaModal={() => {
                  setAreaModalOpen(true);
                }}
                onSave={readOnly ? undefined : handleSave}
                onDelete={!readOnly && onDelete ? handleDelete : undefined}
                showNavigation={
                  onNavigatePrevious != null || onNavigateNext != null
                }
                connectedEventsCheckbox={
                  connectedEventCount > 0 ? (
                    <HotkeySafeCheckbox
                      checked={materialEditScope === "withConnected"}
                      onChange={(checked) => {
                        setMaterialEditScope(
                          checked ? "withConnected" : "eventOnly"
                        );
                      }}
                      label={t("applyMaterialToConnectedEvents", {
                        count: connectedEventCount,
                      })}
                      sx={{ ml: 0 }}
                    />
                  ) : undefined
                }
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {workingEvent.eventType !== "flag" && (
        <AreaSelectionModal
          open={areaModalOpen}
          onClose={() => {
            setAreaModalOpen(false);
          }}
          onSelectArea={handleAreaSelect}
          eventType={workingEvent.eventType}
          areaColors={areaColors}
          disableScopes={SCOPES_TO_DISABLE_AREA_MODAL}
          location={location}
        />
      )}
    </>
  );
};

export const EditEventModal = ({
  open,
  onClose,
  mergedEvent,
  eventToLabellerMapping,
  location,
  onUpdate,
  onDelete,
  onNavigatePrevious,
  onNavigateNext,
  areaColors,
  minTimingBound,
  maxTimingBound,
  readOnly,
  readOnlyReason,
}: EditEventModalProps) => {
  usePauseWhileOpen(open);
  useDisableHotkeyScopes(LABELLING_HOTKEY_SCOPES, open, EDIT_EVENT_SCOPE);

  if (!mergedEvent || !onUpdate || !location) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      sx={{ height: "100vh" }}
    >
      <HotkeyProvider>
        <VideoControlProvider autoPlay={false}>
          <EditEventModalContent
            mergedEvent={mergedEvent}
            eventToLabellerMapping={eventToLabellerMapping}
            location={location}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onClose={onClose}
            onNavigatePrevious={onNavigatePrevious}
            onNavigateNext={onNavigateNext}
            areaColors={areaColors}
            minTimingBound={minTimingBound}
            maxTimingBound={maxTimingBound}
            readOnly={readOnly}
            readOnlyReason={readOnlyReason}
          />
        </VideoControlProvider>
      </HotkeyProvider>
    </Dialog>
  );
};
